/**
 * scrollDebug — staging-only instrumentation for the iOS PWA "fixed nav floats
 * mid-page" bug reported 2026-04-23. Suspect: CollapsibleSection toggles +
 * transition-all + large document-height changes breaking position:fixed.
 *
 * Activation: only on staging hosts AND when isPWA-like (body.pwa-standalone).
 * Produces: localStorage ring buffer (cap 200 entries), dumped via
 *   window.__helmDumpScrollDebug() and cleared via window.__helmClearScrollDebug().
 *
 * This file is import-only in index.tsx — no build-time cost if not imported.
 */

const STORAGE_KEY = 'helm_scroll_debug_v1';
const MAX_ENTRIES = 200;

type Entry = {
  t: number;
  kind: string;
  data?: Record<string, unknown>;
};

declare global {
  interface Window {
    __helmDumpScrollDebug?: () => Entry[];
    __helmClearScrollDebug?: () => void;
    __helmScrollDebugMark?: (kind: string, data?: Record<string, unknown>) => void;
  }
}

function isStaging(): boolean {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h.includes('staging') || h.includes('submitlist.space');
}

function isPWALike(): boolean {
  if (typeof document === 'undefined') return false;
  return document.body?.classList.contains('pwa-standalone') ?? false;
}

function loadBuffer(): Entry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Entry[]) : [];
  } catch {
    return [];
  }
}

function push(kind: string, data?: Record<string, unknown>): void {
  try {
    const buf = loadBuffer();
    buf.push({ t: Date.now(), kind, data });
    if (buf.length > MAX_ENTRIES) buf.splice(0, buf.length - MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(buf));
  } catch {
    // localStorage quota or disabled — swallow silently
  }
}

function offsetParentTag(el: Element | null): string {
  if (!el) return 'null';
  const op = (el as HTMLElement).offsetParent;
  if (!op) return 'null';
  const tag = op.tagName.toLowerCase();
  const id = (op as HTMLElement).id ? `#${(op as HTMLElement).id}` : '';
  const cls = op.className && typeof op.className === 'string'
    ? `.${op.className.split(/\s+/).slice(0, 2).join('.')}`
    : '';
  return `${tag}${id}${cls}`;
}

function snapshotNav(selector: string, label: string): Record<string, unknown> | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  const cs = window.getComputedStyle(el);
  return {
    label,
    position: cs.position,
    top: cs.top,
    bottom: cs.bottom,
    transform: cs.transform,
    offsetParent: offsetParentTag(el),
  };
}

function captureNavState(reason: string): void {
  push('nav-snapshot', {
    reason,
    navbar: snapshotNav('.navbar', 'top'),
    bottomNav: snapshotNav('nav[aria-label="Bottom navigation"]', 'bottom'),
    scrollY: window.scrollY,
    docHeight: document.documentElement.scrollHeight,
    viewportHeight: window.innerHeight,
  });
}

function observeBodyMutations(): MutationObserver {
  const target = document.body;
  const html = document.documentElement;
  const interesting = (attr: string): boolean =>
    attr === 'style' || attr === 'class';

  const handler: MutationCallback = (records) => {
    for (const r of records) {
      if (r.type !== 'attributes' || !r.attributeName) continue;
      if (!interesting(r.attributeName)) continue;
      const el = r.target as HTMLElement;
      push('body-mutation', {
        element: el === html ? 'html' : 'body',
        attribute: r.attributeName,
        current: r.attributeName === 'style' ? el.style.cssText : el.className,
      });
    }
  };

  const observer = new MutationObserver(handler);
  observer.observe(target, { attributes: true, attributeFilter: ['style', 'class'] });
  observer.observe(html, { attributes: true, attributeFilter: ['style', 'class'] });
  return observer;
}

export function initScrollDebug(): void {
  if (typeof window === 'undefined') return;
  if (!isStaging()) return;
  if (!isPWALike()) {
    // body.pwa-standalone is set async in index.tsx; defer one tick
    setTimeout(() => {
      if (isPWALike()) initScrollDebug();
    }, 500);
    return;
  }
  if (window.__helmDumpScrollDebug) return; // already initialized

  push('init', { userAgent: navigator.userAgent, href: window.location.href });

  const observer = observeBodyMutations();

  // Sample nav state every 2s while tab is visible
  const sampler = window.setInterval(() => {
    if (document.visibilityState === 'visible') captureNavState('tick');
  }, 2000);

  const onVisibilityChange = (): void => {
    push('visibility', { state: document.visibilityState });
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  // Capture around scroll starts (user-driven)
  let lastScrollLog = 0;
  const onScroll = (): void => {
    const now = Date.now();
    if (now - lastScrollLog < 1000) return; // throttle to once/sec
    lastScrollLog = now;
    captureNavState('scroll');
  };
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });

  // External markers — CollapsibleSection calls this via window.__helmScrollDebugMark
  window.__helmScrollDebugMark = (kind: string, data?: Record<string, unknown>) => {
    push(kind, data);
    captureNavState(`mark:${kind}`);
  };

  window.__helmDumpScrollDebug = () => {
    const buf = loadBuffer();
    // eslint-disable-next-line no-console
    console.log(`[helm] scroll debug buffer (${buf.length} entries):`, buf);
    return buf;
  };

  window.__helmClearScrollDebug = () => {
    localStorage.removeItem(STORAGE_KEY);
    // eslint-disable-next-line no-console
    console.log('[helm] scroll debug buffer cleared');
  };

  // Stop sampling + disconnect observers on unload; the ring buffer persists
  window.addEventListener('beforeunload', () => {
    clearInterval(sampler);
    observer.disconnect();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    document.removeEventListener('scroll', onScroll, { capture: true });
  });
}
